<template>
  <teleport to="body">
    <div :class="fadeTransition" class="modal">
      <div :class="bounceTransition" class="modal-body">
        <div class="close-svg" @click="closeModal">
          <CloseSVG :propId="'print-close'" :fillColor="'#f7f9fc'" />
        </div>
        <h1 class="modal-header">
          <div id="title">
            <img src="@/assets/icons/question.svg" />
            <span>Printing Help</span>
          </div>
          <p>Please check the boxes below to indicate that you've completed each step</p>
        </h1>
        <div class="modal-content">
          <div id="help-steps">
            <div class="help-step">
              <div class="help-number"><span>1</span></div>
              <img src="@/assets/printing-assets/help-step-1.png" />
              <div class="text-wrap">
                <h2>Paper Size</h2>
                <p>
                  Select the correct paper size of
                  <strong>{{ currentLabel.sheet_size_title }}</strong> for your label sheet.
                </p>
                <div class="step-check" v-if="clientDimensions.width <= 767">
                  <input
                    @change="handleChange('size')"
                    ref="size"
                    id="size"
                    type="checkbox"
                  /><label for="size">I've set my paper size</label>
                </div>
              </div>
            </div>
            <div class="help-step">
              <div class="help-number"><span>2</span></div>
              <img src="@/assets/printing-assets/help-step-2.png" />
              <div class="text-wrap">
                <h2>Scale</h2>
                <p>
                  Set your scale to <strong>Actual Size</strong> or <strong>100%</strong>. Do not
                  shrink to fit your page.
                </p>
                <div class="step-check" v-if="clientDimensions.width <= 767">
                  <input
                    @change="handleChange('scale')"
                    ref="scale"
                    id="scale"
                    type="checkbox"
                  /><label for="scale">I've set the proper scale</label>
                </div>
              </div>
            </div>
            <div class="help-step">
              <div class="help-number"><span>3</span></div>
              <img src="@/assets/printing-assets/help-step-3.png" />
              <div class="text-wrap">
                <h2>Paper Weight</h2>
                <p>
                  Set your <strong>Paper</strong> or <strong>Media Type</strong> to
                  <strong>Labels</strong> or a similar setting such as
                  <strong>Heavyweight Paper</strong>.
                </p>
                <div class="step-check" v-if="clientDimensions.width <= 767">
                  <input
                    @change="handleChange('weight')"
                    ref="weight"
                    id="weight"
                    type="checkbox"
                  /><label for="weight">I've set my paper weight</label>
                </div>
              </div>
            </div>
            <div class="help-step">
              <div class="help-number"><span>4</span></div>
              <img src="@/assets/printing-assets/help-step-4.png" />
              <div class="text-wrap">
                <h2>Labels Loaded</h2>
                <p>
                  Load your labels so that the <a>registration marks</a> print on the
                  <strong>top right</strong> of your sheet.
                </p>
                <div class="step-check" v-if="clientDimensions.width <= 767">
                  <input
                    @change="handleChange('loaded')"
                    ref="loaded"
                    id="loaded"
                    type="checkbox"
                  /><label for="loaded">I've loaded my labels</label>
                </div>
              </div>
            </div>
          </div>
          <div id="step-checks" v-if="clientDimensions.width > 767">
            <div class="step-check">
              <input @change="handleChange('size')" ref="size" id="size" type="checkbox" /><label
                for="size"
                >I've set my paper size</label
              >
            </div>
            <div class="step-check">
              <input @change="handleChange('scale')" ref="scale" id="scale" type="checkbox" /><label
                for="scale"
                >I've set the proper scale</label
              >
            </div>
            <div class="step-check">
              <input
                @change="handleChange('weight')"
                ref="weight"
                id="weight"
                type="checkbox"
              /><label for="weight">I've set my paper weight</label>
            </div>
            <div class="step-check">
              <input
                @change="handleChange('loaded')"
                ref="loaded"
                id="loaded"
                type="checkbox"
              /><label for="loaded">I've loaded my labels</label>
            </div>
          </div>
        </div>
        <div id="help-btns">
          <div>Did these tips help your labels print correctly?</div>
          <button @click="closeModal">Yes, my problem is fixed</button>
          <button :disabled="!allStepsChecked" @click="openPrintingIssues">
            No, I need more help
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, computed, ref, Ref, inject, watch, nextTick } from "vue";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { SheetLabel, ClientDimensions } from "@/types";
import { ActionTypes } from "@/store/actions";

export default defineComponent({
  name: "PrintingHelp",
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const openPrintingIssues = () => {
      store.dispatch(ActionTypes.SET_MODAL, "troubleShooting");
    };

    const currentLabel = computed<SheetLabel>(() => store.getters.currentLabel);
    const clientDimensions = computed<ClientDimensions>(() => store.getters.clientDimensions);

    const size = ref<HTMLInputElement>();
    const scale = ref<HTMLInputElement>();
    const weight = ref<HTMLInputElement>();
    const loaded = ref<HTMLInputElement>();

    const sizeStep = ref(false);
    const scaleStep = ref(false);
    const weightStep = ref(false);
    const loadedStep = ref(false);

    const allStepsChecked = computed<boolean>(
      () => sizeStep.value && scaleStep.value && weightStep.value && loadedStep.value
    );

    watch(clientDimensions, async (newDimension, oldDimension) => {
      const newWidth = newDimension.width;
      const oldWidth = oldDimension.width;

      if ((newWidth <= 767 && oldWidth > 767) || (newWidth > 767 && oldWidth <= 767)) {
        await nextTick();
        handleResize();
      }
    });

    const handleResize = () => {
      setInputChecked(size, sizeStep);
      setInputChecked(scale, scaleStep);
      setInputChecked(weight, weightStep);
      setInputChecked(loaded, loadedStep);
    };

    const setInputChecked = (element: Ref<HTMLInputElement | undefined>, step: Ref<boolean>) => {
      if (element.value) {
        step.value ? (element.value.checked = true) : (element.value.checked = false);
      }
    };

    const handleChange = (step: string) => {
      switch (step) {
        case "size": {
          sizeStep.value = !sizeStep.value;
          break;
        }
        case "scale": {
          scaleStep.value = !scaleStep.value;
          break;
        }
        case "weight": {
          weightStep.value = !weightStep.value;
          break;
        }
        case "loaded": {
          loadedStep.value = !loadedStep.value;
          break;
        }
      }
    };

    return {
      size,
      scale,
      weight,
      loaded,
      sizeStep,
      scaleStep,
      weightStep,
      loadedStep,
      allStepsChecked,
      currentLabel,
      clientDimensions,
      bounceTransition,
      fadeTransition,
      closeModal,
      openPrintingIssues,
      handleChange,
    };
  },
});
</script>

<style scoped>
.modal-body {
  height: 75%;
  max-width: 1000px;
  max-height: 700px;
  overflow: auto;
  justify-content: space-between;
}

.modal-header {
  color: #000;
  padding: 4rem 4rem 0 4rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

#title {
  display: flex;
  align-items: center;
  font-size: 3rem;
  padding-bottom: 1rem;
}

#title img {
  height: 3rem;
  margin-right: 1rem;
}

.modal-header p {
  color: #424753;
  font-size: 1.25rem;
}

.modal-content {
  font-size: 1.5rem;
}

#help-steps {
  display: flex;
  justify-content: space-between;
  padding: 4rem 4rem 0 4rem;
}

.help-step {
  width: 22.5%;
  position: relative;
}

.help-number {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 25px;
  height: 25px;
  left: 10px;
  top: 10px;
  color: #fff;
  border-radius: 50%;
  background: var(--blue) 0% 0% no-repeat padding-box;
  background: #0096ff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
}

.help-step img {
  width: 100%;
  height: auto;
}

.help-step h2 {
  font-size: 2rem;
}

.text-wrap {
  padding-top: 2rem;
}

#help-inputs {
  display: flex;
  justify-content: space-between;
  padding: 4rem;
}

#step-checks {
  display: flex;
  justify-content: space-between;
  padding: 4rem;
}

.step-check {
  width: 22.5%;
  font-weight: 700;
}

.step-check input {
  margin-right: 1rem;
}

#help-btns {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4rem;
  color: #0096ff;
  background-color: #f7f9fc;
  font-size: 2rem;
  font-weight: 700;
}

#help-btns div,
#help-btns button {
  width: 30%;
}

#help-btns button {
  border: 1px solid transparent;
  border-radius: 6px;
  height: 60px;
  color: #fff;
  background-color: #00af43;
  font-size: 1.5rem;
  font-weight: 700;
}

#help-btns button:nth-child(2):hover {
  background: transparent linear-gradient(180deg, #75dd95 0%, var(--green) 100%) 0% 0% no-repeat
    padding-box;
  background: transparent linear-gradient(180deg, #75dd95 0%, #00a753 100%) 0% 0% no-repeat
    padding-box;
}

#help-btns button:last-child {
  background-color: #cc0e2e;
}

#help-btns button:last-child:hover {
  background: transparent linear-gradient(180deg, #ed0c0c 0%, var(--red) 100%) 0% 0% no-repeat
    padding-box;
  background: transparent linear-gradient(180deg, #ed0c0c 0%, #cc0e2e 100%) 0% 0% no-repeat
    padding-box;
}

#help-btns button:last-child:disabled {
  opacity: 0.4;
  color: #fff;
  background-color: #cc0e2e;
}

@media (max-width: 1000px) {
  .modal-body {
    width: 90%;
  }
}

@media (max-width: 767px) {
  .modal-header {
    padding: 2rem 2rem 0 2rem;
  }

  #help-steps,
  #help-btns {
    padding: 2rem;
  }

  #help-steps {
    padding-top: 0rem;
    flex-direction: column;
  }

  .help-step {
    width: 100%;
    margin-top: 4rem;
    font-size: 2rem;
  }

  .help-step p {
    font-size: 1.5rem;
  }

  .help-step:first-child {
    padding-top: 0rem;
  }

  .step-check {
    width: 100%;
    font-size: 1.75rem;
    padding-top: 1rem;
  }

  #help-btns {
    flex-direction: column;
  }

  #help-btns div,
  #help-btns button {
    width: 100%;
  }

  #help-btns button:nth-child(2) {
    margin: 2rem 0;
  }
}
</style>
